
import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Song } from '../types';

const SongsPage: React.FC = () => {
  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentSongId, setCurrentSongId] = useState<string | null>(null);
  
  // Explicitly typing the formData state to allow all valid Song status values
  const [formData, setFormData] = useState<{
    title: string;
    artist: string;
    album: string;
    genre: string;
    status: Song['status'];
    is_explicit: boolean;
    is_featured: boolean;
    allow_download: boolean;
    release_year: number;
  }>({
    title: '',
    artist: '',
    album: '',
    genre: '',
    status: 'published',
    is_explicit: false,
    is_featured: false,
    allow_download: true,
    release_year: new Date().getFullYear(),
  });
  
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchSongs();
  }, []);

  const fetchSongs = async () => {
    setLoading(true);
    const { data } = await supabase.from('songs').select('*').order('created_at', { ascending: false });
    if (data) setSongs(data);
    setLoading(false);
  };

  const handleOpenAdd = () => {
    setFormData({
      title: '',
      artist: '',
      album: '',
      genre: '',
      status: 'published',
      is_explicit: false,
      is_featured: false,
      allow_download: true,
      release_year: new Date().getFullYear(),
    });
    setAudioFile(null);
    setCoverFile(null);
    setIsEditing(false);
    setShowModal(true);
  };

  const handleOpenEdit = (song: Song) => {
    setFormData({
      title: song.title,
      artist: song.artist,
      album: song.album || '',
      genre: song.genre || '',
      status: song.status,
      is_explicit: song.is_explicit,
      is_featured: song.is_featured,
      allow_download: song.allow_download,
      release_year: song.release_year || new Date().getFullYear(),
    });
    setCurrentSongId(song.id);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      let audioUrl = '';
      let coverUrl = '';

      if (audioFile) {
        const audioPath = `tracks/${Date.now()}_${audioFile.name}`;
        const { error: aErr } = await supabase.storage.from('music').upload(audioPath, audioFile);
        if (aErr) throw aErr;
        audioUrl = supabase.storage.from('music').getPublicUrl(audioPath).data.publicUrl;
      }

      if (coverFile) {
        const coverPath = `covers/${Date.now()}_${coverFile.name}`;
        const { error: cErr } = await supabase.storage.from('music').upload(coverPath, coverFile);
        if (cErr) throw cErr;
        coverUrl = supabase.storage.from('music').getPublicUrl(coverPath).data.publicUrl;
      }

      const songData: any = {
        ...formData,
        updated_at: new Date().toISOString(),
      };

      if (audioUrl) songData.file_url = audioUrl;
      if (coverUrl) songData.cover_url = coverUrl;

      if (isEditing && currentSongId) {
        const { error } = await supabase.from('songs').update(songData).eq('id', currentSongId);
        if (error) throw error;
      } else {
        if (!audioFile && !isEditing) throw new Error("Audio file required for new tracks");
        // Omit play_count, like_count, download_count - DB handles these defaults
        const { error } = await supabase.from('songs').insert({
          ...songData,
          uploaded_by: user.id
        });
        if (error) throw error;
      }

      setShowModal(false);
      fetchSongs();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setUploading(false);
    }
  };

  const deleteSong = async (id: string) => {
    if (!confirm('Permanently delete this track from library?')) return;
    const { error } = await supabase.from('songs').delete().eq('id', id);
    if (!error) fetchSongs();
    else alert("Delete failed. Ensure the track isn't in any playlists.");
  };

  const filtered = songs.filter(s => 
    s.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
    s.artist.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-10 animate-in fade-in duration-700">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h2 className="text-4xl font-black text-white tracking-tighter uppercase">Music</h2>
          <p className="text-slate-500 font-bold uppercase tracking-[0.3em] text-[10px] mt-2">Core Repository Management</p>
        </div>
        <button 
          onClick={handleOpenAdd}
          className="bg-accent-blue hover:bg-accent-blue/90 text-white px-8 py-4 rounded-[1.5rem] font-black text-[11px] uppercase tracking-[0.2em] flex items-center space-x-3 shadow-2xl transition-all border border-white/5 active:scale-95"
        >
          <i className="fas fa-plus text-sm"></i>
          <span>Add New Track</span>
        </button>
      </div>

      <div className="glass rounded-[3rem] shadow-2xl border-white/5 overflow-hidden">
        <div className="p-8 border-b border-white/5 bg-white/[0.01]">
          <div className="relative group max-w-xl">
            <input 
              type="text" 
              placeholder="Filter by title, artist, or genre..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#020617] border border-white/5 text-white px-14 py-5 rounded-[1.8rem] focus:ring-2 focus:ring-accent-blue/40 transition-all font-bold placeholder-slate-600 shadow-inner"
            />
            <i className="fas fa-magnifying-glass absolute left-6 top-1/2 -translate-y-1/2 text-slate-600"></i>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-slate-500 font-black text-[11px] uppercase tracking-[0.2em] border-b border-white/5 bg-white/[0.01]">
                <th className="px-10 py-6">Track Info</th>
                <th className="px-6 py-6">Details</th>
                <th className="px-6 py-6">Status</th>
                <th className="px-6 py-6 text-center">Plays</th>
                <th className="px-10 py-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading ? (
                [1,2,3].map(i => <tr key={i}><td colSpan={5} className="h-24 animate-pulse bg-white/[0.01]"></td></tr>)
              ) : filtered.map(song => (
                <tr key={song.id} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="px-10 py-6">
                    <div className="flex items-center space-x-5">
                      <div className="relative">
                        <img 
                          src={song.cover_url || `https://picsum.photos/seed/${song.id}/120/120`} 
                          className="w-14 h-14 rounded-2xl object-cover shadow-xl border border-white/10"
                          alt=""
                        />
                        {song.is_explicit && (
                          <span className="absolute -top-2 -right-2 bg-red-600 text-[8px] font-black text-white px-1.5 py-0.5 rounded-full border border-navy-950">E</span>
                        )}
                      </div>
                      <div>
                        <p className="font-extrabold text-white text-[15px] tracking-tight">{song.title}</p>
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-1">{song.artist}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-6 text-xs font-bold text-slate-400">{song.genre || 'Pop'}</td>
                  <td className="px-6 py-6">
                    <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                      song.status === 'published' ? 'bg-green-500/10 text-green-500 border-green-500/20' : 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
                    }`}>
                      {song.status}
                    </span>
                  </td>
                  <td className="px-6 py-6 text-center font-black text-white">{(song.play_count || 0).toLocaleString()}</td>
                  <td className="px-10 py-6 text-right space-x-2">
                    <button onClick={() => handleOpenEdit(song)} className="w-10 h-10 rounded-xl bg-white/5 text-slate-400 hover:text-accent-cyan transition-all">
                      <i className="fas fa-pen-to-square"></i>
                    </button>
                    <button onClick={() => deleteSong(song.id)} className="w-10 h-10 rounded-xl bg-white/5 text-slate-400 hover:text-red-500 transition-all">
                      <i className="fas fa-trash-can"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-[#020617]/95 backdrop-blur-sm animate-in fade-in duration-300 overflow-y-auto">
          <div className="glass w-full max-w-2xl rounded-[3rem] shadow-2xl my-auto animate-in zoom-in-95 duration-500">
            <div className="p-10 border-b border-white/5 flex justify-between items-center bg-white/[0.01]">
              <div>
                <h3 className="text-2xl font-black text-white uppercase tracking-tighter">{isEditing ? 'Update Track' : 'New Library Entry'}</h3>
                <p className="text-slate-500 font-bold uppercase tracking-widest text-[9px] mt-1">Advanced Music Schema</p>
              </div>
              <button onClick={() => setShowModal(false)} className="w-12 h-12 rounded-2xl bg-white/5 text-slate-400 hover:text-white transition-all">
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-10 space-y-8">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Track Title</label>
                  <input required type="text" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="w-full bg-[#020617] border border-white/5 text-white px-6 py-4 rounded-2xl focus:ring-2 focus:ring-accent-blue/40" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Artist Name</label>
                  <input required type="text" value={formData.artist} onChange={e => setFormData({...formData, artist: e.target.value})} className="w-full bg-[#020617] border border-white/5 text-white px-6 py-4 rounded-2xl focus:ring-2 focus:ring-accent-blue/40" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Genre</label>
                  <select value={formData.genre} onChange={e => setFormData({...formData, genre: e.target.value})} className="w-full bg-[#020617] border border-white/5 text-white px-6 py-4 rounded-2xl focus:ring-2 focus:ring-accent-blue/40">
                    <option value="">Select Genre</option>
                    <option value="Pop">Pop</option>
                    <option value="Rock">Rock</option>
                    <option value="Electronic">Electronic</option>
                    <option value="Hip Hop">Hip Hop</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Status</label>
                  <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value as any})} className="w-full bg-[#020617] border border-white/5 text-white px-6 py-4 rounded-2xl focus:ring-2 focus:ring-accent-blue/40">
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                    <option value="flagged">Flagged</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center space-x-10 p-6 bg-white/[0.02] rounded-[2rem] border border-white/5">
                <label className="flex items-center space-x-3 cursor-pointer group">
                  <input type="checkbox" checked={formData.is_explicit} onChange={e => setFormData({...formData, is_explicit: e.target.checked})} className="hidden" />
                  <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${formData.is_explicit ? 'bg-red-600 border-red-600' : 'border-white/10 group-hover:border-red-500/50'}`}>
                    {formData.is_explicit && <i className="fas fa-check text-white text-[10px]"></i>}
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">Explicit</span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer group">
                  <input type="checkbox" checked={formData.is_featured} onChange={e => setFormData({...formData, is_featured: e.target.checked})} className="hidden" />
                  <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${formData.is_featured ? 'bg-accent-cyan border-accent-cyan' : 'border-white/10 group-hover:border-accent-cyan/50'}`}>
                    {formData.is_featured && <i className="fas fa-check text-navy-950 text-[10px]"></i>}
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">Featured</span>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Audio File</label>
                  <input type="file" accept="audio/*" onChange={e => setAudioFile(e.target.files?.[0] || null)} className="w-full bg-white/5 border border-dashed border-white/10 p-4 rounded-2xl text-[10px] text-slate-400" />
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Cover Art</label>
                  <input type="file" accept="image/*" onChange={e => setCoverFile(e.target.files?.[0] || null)} className="w-full bg-white/5 border border-dashed border-white/10 p-4 rounded-2xl text-[10px] text-slate-400" />
                </div>
              </div>

              <button disabled={uploading} type="submit" className="w-full bg-accent-blue text-white py-5 rounded-[1.8rem] font-black text-xs uppercase tracking-widest shadow-2xl transition-all disabled:opacity-50">
                {uploading ? 'Processing Transaction...' : (isEditing ? 'Commit Changes' : 'Initialize Library Entry')}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SongsPage;
